/* eslint-disable max-len */
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { NAVBAR_HEIGHT } from 'library/globals';
import * as React from 'react';
import { ReduxStore } from 'reducers/redux.types';
import { Master } from 'types/master-data-types';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import { useTranslation } from 'react-i18next';
import FilterBlue from '../../assets/filter-blue';
import {
    Dropdown,
    Menu,
    Table,
    Tag,
    Input,
    Select,
    DatePicker,
    Button,
    Drawer,
    message,
} from 'antd';
import { getInvoices } from '../../network/common.api';
import {
    EllipsisOutlined,
    LeftOutlined,
    RightOutlined,
    SearchOutlined,
    VerticalRightOutlined,
} from '@ant-design/icons';
import moment, { Moment } from 'moment';

const { Option } = Select;

const invoicePageStyles = (theme: ThemeType) => ({
    main: {
        width: '100%',
        padding: '0px 8px',
        '& .ant-table-container table > thead > tr:first-child th:first-child': {
            padding: '8px 0px',
        },
        '& .ant-table-thead > tr > th': {
            padding: '8px 8px',
            borderRight: 'none',
            backgroundColor: '#EFF4FC',
            color: '#333333',
            fontFamily: 'Open Sans',
            fontSize: 12,
            letterSpacing: 0,
            whiteSpace: 'pre-wrap',
            lineHeight: '12px',
        },
        '& .ant-table-tbody > tr > td': {
            padding: '12px 8px',
        },
        '& .ant-dropdown-menu-submenu-popup ul, .ant-dropdown-menu-submenu-popup li': {
            width: 80,
        },
        '& .ant-table-thead > tr > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before': {
            backgroundColor: 'inherit',
        },
        '& .ant-select:not(.ant-select-customize-input) .ant-select-selector, .ant-input': {
            width: 100,
            borderRadius: 12,
            border: '1px solid #999999',
            height: 24,
            fontSize: 12,
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
        },
        '& .ant-select': {
            marginRight: 24,
            marginLeft: 24,
        },
        '& .ant-table-selection': {
            marginLeft: 8,
            marginRight: 8,
        },
        '& .ant-picker-range-separator, .ant-picker-input': {
            margin: 0,
            padding: 0,
        },
        backgroundColor: theme.backgroundColor,
    },
    title: {
        width: '100%',
        borderRadius: '0 2px 0 0',
        backgroundColor: '#FFFFFF',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 4,
        fontSize: 16,
        color: '#111111',
        fontWeight: 'bold',
    },
    pageFilter: {
        display: 'flex',
        marginBottom: 8,
        borderBottom: '1px solid #D9D9D9',
    },
    pageName: {
        fontFamily: 'Open Sans',
        fontSize: 14,
        letterSpacing: 0,
        color: '#666666',
        padding: '12px 50px',
        cursor: 'pointer',
    },
    selectedPage: {
        fontSize: 14,
        fontFamily: 'Open Sans',
        fontWeight: 'bold',
        letterSpacing: 0,
        color: theme.primaryColor,
        padding: '12px 50px',
        borderBottom: `4px solid ${theme.primaryColor}`,
    },
    filters: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        '& .ant-picker': {
            width: 180,
            border: '1px solid #999999',
            height: 24,
            fontSize: 9,
            borderRadius: 12,
        },
        '& .ant-picker-input > input': {
            fontSize: 12,
        },
    },
    actionButton: {
        marginRight: 8,
    },
    count: {
        height: 18,
        padding: 10,
        borderRadius: 9,
        backgroundColor: '#E9E9E9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        fontSize: 12,
        color: '#333',
        fontWeight: 600,
    },
    filter: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 24,
    },
    filterText: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        marginRight: 8,
    },
    countSelected: {
        backgroundColor: theme.primaryColor,
        color: '#FFF',
        fontWeight: 'bold',
    },
    cellValue: {
        color: '#111111',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
        cursor: 'pointer',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    notesCellValue: {
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#111111',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
        cursor: 'pointer',
    },
    table: {
        '& .ant-table table': {
            backgroundColor: '#FFF',
        },
    },
    tableRowColor: {
        '& .ant-table table': {
            backgroundColor: '#FFF',
        },
        '&& tbody > tr:hover > td': {
            background: '#E7F5FF',
        },
    },
    cellNa: {
        color: '#999999',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
    },
    ellipsisIcon: {
        fontSize: 20,
    },
    tabsContainer: {
        '& .ant-tabs-bar': {
            margin: '0px',
        },
        '& .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab, .ant-tabs-card > div > .ant-tabs-nav .ant-tabs-tab': {
            padding: 4,
        },
        '& .ant-tabs-top > .ant-tabs-nav, .ant-tabs-bottom > .ant-tabs-nav, .ant-tabs-top > div > .ant-tabs-nav, .ant-tabs-bottom > div > .ant-tabs-nav': {
            margin: 0,
        },
        '& .ant-tabs-tab': {
            color: '#666666',
            minWidth: 175,
            fontFamily: 'Open Sans',
            fontSize: 14,
            letterSpacing: 0,
            padding: '2px 8px',
            marginRight: 2,
            borderRadius: '4px 4px 0 0',
            backgroundColor: '#F5F5F5',
            cursor: 'pointer',
        },
        '& .ant-tabs-dropdown-menu-item': {
            color: '#666666',
            minWidth: 175,
            fontFamily: 'Open Sans',
            fontSize: 14,
            letterSpacing: 0,
            padding: '2px 8px',
            marginRight: 2,
            backgroundColor: '#F5F5F5',
            cursor: 'pointer',
        },
        '& .ant-tabs-tab.ant-tabs-tab-active': {
            color: '#111111',
            fontWeight: 'bold',
            backgroundColor: '#FFF',
        },
        '& .ant-tabs-tab-btn': {
            display: 'flex',
            width: '100%',
            padding: '0px 6px',
            justifyContent: 'space-between',
        },
        '& .ant-tabs.ant-tabs-card .ant-tabs-card-bar .ant-tabs-tab': {
            marginRight: 0,
        },
        '& .ant-tabs-nav-more span': {
            transform: 'rotate(90deg)',
        },
    },
    bucket: {
        color: '#666666',
        fontFamily: 'Open Sans',
        fontSize: 14,
        letterSpacing: 0,
        padding: '6px 12px',
        marginRight: 2,
        borderRadius: '4px 4px 0 0',
        backgroundColor: '#F5F5F5',
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    moreFilter: {
        fontFamily: 'Open Sans',
        marginLeft: 1,
        fontWeight: 600,
        fontSize: 12,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    resetFilter: {
        color: '#DF1D1D',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        marginLeft: 16,
        fontFamily: 'Open Sans',
    },
    bucketRow: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        overflow: 'scroll',
    },
    extraFilters: {
        width: '100%',
        borderRadius: '0 2px 0 0',
        backgroundColor: '#FFFFFF',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 4,
        flexDirection: 'row',
    },
    leftFilters: {
        display: 'flex',
        alignItems: 'center',
    },
    rightFilters: {
        display: 'flex',
        alignItems: 'center',
        '& .ant-btn': {
            boxSizing: 'border-box',
            height: 32,
            borderRadius: 3,
            fontFamily: 'Open Sans',
            fontSize: 12,
            fontWeight: 600,
            laterSpacing: 0,
        },
        '& .ant-btn-ghost': {
            border: `1px solid ${theme.primaryColor} `,
            color: theme.primaryColor,
        },
        '& .ant-btn-primary, .ant-btn-primary:hover, .ant-btn-primary:focus': {
            backgroundColor: theme.primaryColor,
            color: '#FFFFFF',
        },
        '& .ant-dropdown-menu-submenu-title': {
            padding: '12px 4px',
        },
    },
    downloadBtn: {
        marginRight: 8,
    },
    settingIcon: {
        color: '#333333',
        fontSize: 20,
        cursor: 'pointer',
        marginLeft: 8,
    },
    menu: {
        fontSize: 14,
        fontWeight: 'bold',
    },
    modalContainer: {
        padding: '1rem 0',
        paddingBottom: '3rem',
        minWidth: '40%',
    },
    carouselContainer: {
        padding: '1.5rem',
        maxWidth: '100%',
        '& span': {
            color: '#222021 !important',
        },
        '& span > svg': {
            width: '120%',
            height: '120%',
        },
    },
    popupbox: {
        backgroundColor: 'white',
        padding: '0px',
        borderRadius: '4px',
    },
    popupheader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#10477C',
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontWeight: 'bold',
        lineHeight: '19px',
        padding: '14px',
    },
    modal: {
        '& .ant-modal-mask': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
        '& .ant-modal-header': {
            backgroundColor: '#082E78',
            borderRadius: '4px 4px 0 0',
        },
        '& .ant-modal-title, .ant-modal-close-x': {
            color: '#FFFFFF',
        },
        '& .ant-modal-content': {
            borderRadius: 4,
            boxShadow: 'none !important',
        },
        '& .ant-modal-body': {
            padding: '0px !important',
        },
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    horizontalline: {
        height: '1px',
        backgroundColor: '#CCCCCC',
    },
    modalBody: {
        padding: '8px',
        fontSize: '13px !important',
        minWidth: 450,
    },
    uploadText: {
        color: '#111111',
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 32,
    },
    closeIcon: {
        lineHeight: '24px',
        margin: '0px 6px',
        padding: '8px',
        width: 40,
        cursor: 'pointer',
        color: '#666666',
        fontWeight: 'bold',
    },
    uploadHint: {
        fontSize: 16,
        color: '#666666',
    },
    modalfooter: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    mdbutton: {
        borderRadius: '2px',
        border: '0 !important',
        margin: '6px 8px',
        fontWeight: 500,
        minHeight: '36px',
        minWidth: '88px',
        textAlign: 'center',
        verticalAlign: 'middle',
        padidng: '0 8px',
    },
    dropdown: {
        display: 'flex',
        justifyContent: 'flex-end',
        flexWrap: 'wrap-reverse',
        alignItems: 'center',
    },
    grayCircle: {
        width: '32px',
        height: '32px',
        fontSize: '14px',
        backgroundColor: '#F5F5F5',
        borderRadius: '64px',
        marginRight: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    customFlex: {
        display: 'flex', alignItems: 'center',
    },
    searchInput: {
        width: '100%',
        marginRight: '1%',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 600,
        fontSize: 20,
        color: '#333',
        width: '100%',
        padding: 0,
        justifyContent: 'space-between',
        fontFamily: 'Open Sans',
        letterSpacing: 0,
    },
    currentPage: {
        boxSizing: 'border-box',
        height: 22,
        width: 32,
        border: '1px solid #999999',
        borderRadius: 3,
        color: '#111111',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
        textAlign: 'center',
    },
});

interface InvoicePageProps extends StylesProps<ReturnType<typeof invoicePageStyles>> {
    master: Master,
    filter_metadata: any,
}

const Invoices = (props: InvoicePageProps) => {
    const {
        classes,
        master,
        filter_metadata,
    } = props;
    const { t } = useTranslation();
    const [addressesSelected, setAddressesSelected] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [invoices, setInvoices] = React.useState<any[]>([]);
    const [searchString, setSearchString] = React.useState<string>('');
    const [dateType, setDateType] = React.useState('created'); // Default value
    const [visible, setVisible] = React.useState(false);
    const [servicetype, setServicetype] = React.useState([]);
    const [invoiceStatus, setInvoiceStatus] = React.useState('');
    const [cnStatus, setCnStatus] = React.useState<string[]>([]);
    const [serviceTypeList, setServiceTypeList] = React.useState([]);
    const [cnStatusTypeList, setCnStatusTypeList] = React.useState([]);
    const [currentPage, setCurrentPage] = React.useState<number>(1); // New pagination state
    const [pageSize, setPageSize] = React.useState<number>(15);
    const [nextPage, setNextPage] = React.useState<boolean>(false);
    const [filters, setFilters] = React.useState({
        startDate: moment().subtract(7, 'days').unix() * 1000, // Example: 7 days ago
        endDate: moment().unix() * 1000, // Example: today
    });
    const bannerHeight = (master?.config?.customer_portal_config?.password_policy_banner_expiry_date
        && new Date(master?.config?.customer_portal_config?.password_policy_banner_expiry_date) <= new Date()) ? 0 : 50;
    const customerId = master?.Customer?.id;
    const getInvoicesList = async () => {
        setLoading(true);
        // Ensure serviceTypeId is always an array of strings and not null
        const serviceTypeArray = Array.isArray(servicetype) ? servicetype : [servicetype];

        // Ensure cnStatus is always an array of strings and not null
        const cnStatusArray = Array.isArray(cnStatus) ? cnStatus : [cnStatus];

        const response = await getInvoices({
            recordsPerPage: pageSize,
            page: currentPage,
            dateType,
            fromDate: moment(filters.startDate).format('YYYY-MM-DD'),
            toDate: moment(filters.endDate).format('YYYY-MM-DD'),
            clientId: customerId,
            invoiceNumber: searchString,
            serviceTypeId: serviceTypeArray,
            status: cnStatusArray,
            generationStatus: invoiceStatus,
        });
        if (response) {
            setInvoices(response.data.data);
            setNextPage(response.data.isNextPresent);
        } else {
            message.error('Failed to fetch invoices');
        }
        setLoading(false);
    };

    const fetchServiceTypes = async () => {
        const serviceTypes = filter_metadata?.find((filter: any) => filter.id === 'bookingServiceTypeIdList');
        const selectedServiceTypes = serviceTypes.options.map((option: any) => option.value);
        const cnStatusTypes = filter_metadata?.find((filter: any) => filter.id === 'statusList');
        const selectedCnStatus = cnStatusTypes.options.map((option: any) => option.value);
        setCnStatusTypeList(selectedCnStatus);
        setServiceTypeList(selectedServiceTypes);
    };

    React.useEffect(() => {
        getInvoicesList();
    }, [searchString, dateType, filters, currentPage, pageSize]);
    React.useEffect(() => {
        fetchServiceTypes(); // Only call once, not with filters
    }, []);

    const showDrawer = () => {
        setVisible(true);
    };

    const onClose = () => {
        setVisible(false);
    };

    const applyFilters = () => {
        onClose();
        console.log('cnStatus', cnStatus);
        getInvoicesList();
    };

    const renderTitle = () => {
        return (
            <div className={classes.title}>
                {t('invoices')}
            </div>
        );
    };

    const invoiceColumns: Record<string, string> = {
        invoiceNumber: 'Invoice Number',
        invoiceStatus: 'Invoice Status',
        invoiceCreationDate: 'Invoice Creation Date',
        totalInvoiceAmount: 'Invoice Amount',
        balanceAmount: 'Balance Amount',
        serviceTypes: 'Service Types',
        cnStatus: 'CN Status',
        actions: 'Actions',
    };

    const renderText = (text: string | number) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };
    const renderColumn = (text: any, row: any, column: string) => {
        switch (column) {
            case 'invoiceStatus': {
                return row.status ? row.status : '-';
            }
            case 'totalInvoiceAmount': {
                return row.totalInvoiceAmount ? row.totalInvoiceAmount : '-';
            }
            case 'balanceAmount': {
                const amount = (Number(row.totalInvoiceAmount) || 0) - (Number(row.settledAmountForRemittance) || 0);
                return amount;
            }
            case 'actions': {
                // Wrap case in braces to create block scope
                const menu = (
                    <Menu>
                        <Menu.Item>
                            <a href={row.fileLink}>{t('download_excel')}</a>
                        </Menu.Item>
                        <Menu.Item>
                            <a href={row.pdfFileLink}>{t('downloadPDF')}</a>
                        </Menu.Item>
                    </Menu>
                );
                return (
                    <Dropdown overlay={menu} trigger={['hover']}>
                        <EllipsisOutlined style={{ fontSize: '20px', cursor: 'pointer' }} />
                    </Dropdown>
                );
            }
            case 'invoiceCreationDate':
                return row.createdAt ? row.createdAt.slice(0, 10) : '';
            case 'serviceTypes':
                return row.filters.serviceTypeId && row.filters.serviceTypeId.length > 0
                    ? row.filters.serviceTypeId.map((serviceType: string) => (
                        <Tag key={serviceType}>{serviceType}</Tag>
                    ))
                    : '-';

            case 'cnStatus':
                // console.log('hey filter', row.filters.status);
                return row.filters.status && row.filters.status.length > 0
                    ? row.filters.status.map((status: string) => (
                        <Tag key={status}>{status}</Tag>
                    ))
                    : '-';
            default:
                return renderText(text as string);
        }
    };

    const getColumns = (): any[] => {
        const columns: any = Object.keys(invoiceColumns).map((column) => {
            const colConfig = {
                key: column,
                title: t(column),
                dataIndex: column,
                ellipsis: true,
                render: (text: string | Record<string, string> | Moment, row: any) => renderColumn(text, row, column),
            };
            // Add a fixed width for the actions column and make it sticky
            if (column === 'actions') {
                return {
                    ...colConfig,
                    width: 100, // Set the width of the actions column
                    fixed: 'right', // Fix the actions column to the right
                };
            }
            return colConfig;
        });
        return columns;
    };

    const renderTable = () => {
        return (
            <Table
                bordered={false}
                columns={getColumns()}
                rowKey={(row) => row.invoiceNumber}
                dataSource={invoices || []}
                loading={loading}
                pagination={false}
                rowSelection={{
                    onSelect: (row, selected: any) => {
                        let newArray = [...addressesSelected];
                        if (selected && !newArray.includes(row.invoiceNumber)) {
                            newArray.push(row.invoiceNumber);
                        } else {
                            newArray = newArray.filter((item) => (item !== row.invoiceNumber));
                        }
                        setAddressesSelected(newArray);
                    },
                    onSelectAll: (selected, selectedRows) => {
                        const newArray = selectedRows.map((item) => (item.invoiceNumber));
                        setAddressesSelected(newArray);
                    },
                }}
                locale={{
                    emptyText: <div className={classes.cellNa}>-NA-</div>,
                }}
                className={classes.table}
                scroll={{
                    y: `calc(((100vh - ${NAVBAR_HEIGHT}px) - 110px) - ${bannerHeight}px)`,
                }}
            />
        );
    };

    const renderInvoiceFilter = () => {
        const handleInputChange = (event: any) => {
            const invoiceNumber = event.target.value;
            setSearchString(invoiceNumber);
        };
        return (
            <div className={classes.leftFilters}>
                <Input
                    className={classes.searchInput}
                    type="search"
                    value={searchString}
                    onChange={(e: any) => handleInputChange(e)}
                    placeholder="Invoice Number"
                    suffix={<SearchOutlined />}
                />
            </div>
        );
    };

    const renderDateType = () => {
        return (
            <Select
                value={dateType}
                onChange={(value) => setDateType(value)}
                style={{ width: 100 }}
            >
                <Option value="created">Created At</Option>
                <Option value="updated">Updated At</Option>
            </Select>
        );
    };

    const handleDateChange = (value: any) => {
        const date: any = { start: moment().subtract(7, 'd'), end: moment() };
        if (value.length === 2) {
            date.start = value[0];
            date.end = value[1];
        }
        const newFilters = { ...filters };
        newFilters.startDate = date.start.format('YYYY-MM-DD');
        newFilters.endDate = date.end.format('YYYY-MM-DD');
        setFilters(newFilters);
    };

    const renderRangePicker = () => {
        return (
            <div className={classes.filter}>
                <span className={classes.filterText}>Date</span>
                <DatePicker.RangePicker
                    value={[
                        moment(filters.startDate),
                        moment(filters.endDate),
                    ]}
                    format="DD/MM/YYYY"
                    onChange={handleDateChange}
                />
            </div>
        );
    };
    const resetFilters = () => {
        setSearchString('');
        setDateType('created');
        setServicetype([]);
        setInvoiceStatus('');
        setCnStatus([]);
        setFilters({
            startDate: moment().subtract(7, 'days').unix() * 1000, // Example: 7 days ago
            endDate: moment().unix() * 1000, // Example: today
        });
    };

    const renderHeader = () => {
        return (
            <div className={classes.header}>
                <div>
                    <span>
                        {t('more_filters')}
                    </span>
                </div>
                <span
                    className={classes.resetFilter}
                    onClick={() => {
                        onClose();
                        resetFilters();
                    }}
                >
                    {t('reset_filters')}
                </span>
            </div>
        );
    };

    const renderMoreFiltersOption = () => {
        return (
            <>
                <div
                    className={classes.moreFilter}
                    onClick={showDrawer}
                >
                    <FilterBlue style={{ marginRight: 2 }} />
                    <span>{t('more_filters')}</span>
                </div>
                <Drawer
                    title={renderHeader()}
                    visible={visible}
                    onClose={onClose}
                    footer={(
                        <div style={{ textAlign: 'left' }}>
                            <Button type="primary" onClick={applyFilters}>
                                Apply
                            </Button>
                        </div>
                    )}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <label style={{ marginRight: 16 }}>
                            Service Type:
                        </label>
                        <Select
                            placeholder="Select a filter"
                            mode="multiple"
                            style={{ flex: 1 }}
                            value={servicetype}
                            onChange={(value) => setServicetype(value)}
                        >
                            {
                                serviceTypeList.map(
                                    (s) => (
                                        <Option key={s} value={s}>{s}</Option>
                                    ),
                                )
                            }
                        </Select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <label style={{ marginRight: 16 }}>
                            Invoice Status:
                        </label>
                        <Select
                            placeholder="Select a filter"
                            style={{ flex: 1 }}
                            onChange={(value) => setInvoiceStatus(value)}
                        >
                            {
                                [t('all'), t('completed'), t('processing'), t('error'), t('processing_freeze'), t('frozen'), t('invoice_settled'), t('partially_settled')].map(
                                    (s) => (
                                        <Option key={s} value={s}>{s}</Option>
                                    ),
                                )
                            }
                        </Select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <label style={{ marginRight: 16 }}>
                            CN Status:
                        </label>
                        <Select
                            placeholder="Select a filter"
                            mode="multiple"
                            style={{ flex: 1 }}
                            value={cnStatus}
                            onChange={(value) => setCnStatus(value)}
                        >
                            {
                                cnStatusTypeList.map(
                                    (s) => (
                                        <Option key={s} value={s}>{t(s)}</Option>
                                    ),
                                )
                            }
                        </Select>
                    </div>
                </Drawer>
            </>
        );
    };

    const renderResetFilter = () => {
        return (
            <span
                className={classes.resetFilter}
                onClick={resetFilters}
            >
                {t('reset_filters')}
            </span>
        );
    };

    const renderLeftFilters = () => {
        return (
            <div className={classes.leftFilters}>
                {renderInvoiceFilter()}
                {renderDateType()}
                {renderRangePicker()}
                {renderMoreFiltersOption()}
                {renderResetFilter()}
            </div>
        );
    };

    const onPaginationChange = (page:number, pagesize:number) => {
        setCurrentPage(page);
        setPageSize(pagesize);
    };

    const onPreviousClick = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            onPaginationChange(currentPage - 1, pageSize);
        }
    };

    const onNextClick = () => {
        if (nextPage) {
            setCurrentPage(currentPage + 1);
            onPaginationChange(currentPage + 1, pageSize);
        }
    };

    const renderRightFilters = () => {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <VerticalRightOutlined
                    onClick={currentPage !== 1 ? () => onPaginationChange(1, pageSize) : undefined}
                    style={{
                        fontSize: '12px',
                        color: currentPage !== 1 ? '#000000' : '#ddd',
                        cursor: currentPage !== 1 ? 'pointer' : 'default',
                        marginRight: '4px', // Reduced margin
                    }}
                />
                <LeftOutlined
                    onClick={currentPage > 1 ? onPreviousClick : undefined}
                    style={{
                        fontSize: '12px',
                        color: currentPage > 1 ? '#000000' : '#ddd',
                        cursor: currentPage > 1 ? 'pointer' : 'default',
                        marginRight: '4px', // Reduced margin
                    }}
                />
                <div className={classes.currentPage}>
                    {currentPage}
                </div>
                <RightOutlined
                    onClick={nextPage ? onNextClick : undefined}
                    style={{
                        fontSize: '14px',
                        color: nextPage ? '#000000' : '#ddd',
                        cursor: nextPage ? 'pointer' : 'default',
                        marginLeft: '4px', // Reduced margin
                    }}
                />
            </div>
        );
    };

    const renderExtraFilters = () => {
        return (
            <div className={classes.extraFilters}>
                {renderLeftFilters()}
                {renderRightFilters()}
            </div>
        );
    };

    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                {renderExtraFilters()}
            </div>
        );
    };

    return (
        <div className={classes.main} style={{ height: `calc((100vh - ${NAVBAR_HEIGHT}px)` }}>
            {renderTitle()}
            {renderFilters()}
            {renderTable()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const { config } = master;
    const filter_metadata: any = config?.filter_metadata || [];
    return {
        master,
        filter_metadata,
    };
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: invoicePageStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

const InvoicePage = GenericHoc(hocConfig)(Invoices);
export default InvoicePage;

